import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Inbox } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PaymentDialog } from "@/components/PaymentDialog";
import { MyAdRow } from "@/components/myads/MyAdRow";
import { DiscountDialog, PromoteDialog } from "@/components/myads/MyAdDialogs";
import { DraftsTab } from "@/components/myads/DraftsTab";
import { RepostDialog } from "@/components/myads/RepostDialog";

export default function MyAds() {
  const { lang } = useLang();
  const L = (ar, en) => (lang === "en" ? en : ar);
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") === "drafts" ? "drafts" : "ads";
  const [ads, setAds] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [discountAd, setDiscountAd] = useState(null);
  const [promo, setPromo] = useState(null);
  const [intent, setIntent] = useState(null);
  const load = useCallback(() => api.get("/listings/mine").then(({ data }) => setAds(data)).catch(() => setAds([])), []);
  const loadDrafts = useCallback(() => api.get("/drafts").then(({ data }) => setDrafts(data)).catch(() => {}), []);
  useEffect(() => { load(); loadDrafts(); }, [load, loadDrafts]);
  useEffect(() => { const id = sp.get("discount"); const a = id && (ads || []).find((x) => x.id === id); if (a) setDiscountAd(a); }, [ads, sp]);

  const run = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const pay = (ad) => setIntent({ listing_id: ad.id });
  const [repostAd, setRepostAd] = useState(null);
  const on = {
    del: (ad) => window.confirm(L("حذف الإعلان؟", "Delete this ad?")) && run(() => api.delete(`/listings/${ad.id}`), L("تم حذف الإعلان", "Ad deleted")),
    sold: (ad) => (ad.raw_status === "sold" ? run(() => api.post(`/listings/${ad.id}/unsold`), L("تمت إعادة عرض الإعلان", "Ad relisted"))
      : window.confirm(L("هل تريد تحديد هذا الإعلان كمُباع؟ لن يظهر في نتائج البحث.", "Mark this ad as sold? It will be hidden from search.")) && run(() => api.post(`/listings/${ad.id}/sold`), L("تم تحديد الإعلان كمُباع", "Marked as sold"))),
    renew: (ad) => run(async () => {
      const { data } = await api.post(`/listings/${ad.id}/renew`);
      if (data.fee_required) { setIntent({ listing_id: ad.id }); } else toast.success(L("تم تجديد الإعلان", "Ad renewed"));
    }),
    pause: async (ad, paused) => {
      setAds((p) => p.map((x) => (x.id === ad.id ? { ...x, paused } : x)));
      try { await api.post(`/listings/${ad.id}/pause`, { paused }); toast.success(paused ? L("تم إيقاف الإعلان مؤقتاً وإخفاؤه عن الزوار", "Ad paused and hidden from visitors") : L("تمت إعادة نشر الإعلان", "Ad is live again")); }
      catch (err) { setAds((p) => p.map((x) => (x.id === ad.id ? { ...x, paused: !paused } : x))); toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    },
    booking: async (ad, enabled) => {
      setAds((p) => p.map((x) => (x.id === ad.id ? { ...x, booking_enabled: enabled } : x)));
      try { await api.post(`/listings/${ad.id}/booking`, { enabled }); toast.success(enabled ? L("الإعلان متاح لحجز المواعيد", "Ad is open for bookings") : L("تم إيقاف حجز المواعيد لهذا الإعلان", "Bookings paused for this ad")); }
      catch (err) { setAds((p) => p.map((x) => (x.id === ad.id ? { ...x, booking_enabled: !enabled } : x))); toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    },
    discount: setDiscountAd, promote: (ad, renew) => setPromo({ ad, renew }), pay, repost: setRepostAd,
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF8F3]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 w-full py-6 flex-1">
        <h1 className="font-headline text-2xl font-extrabold text-slate-900 mb-4 text-center" data-testid="my-ads-title">{L("إعلاناتي", "My Ads")}</h1>
        <div className="flex gap-2 mb-5 bg-white rounded-2xl p-1 border border-[#EFEBE0]" data-testid="my-ads-tabs">
          {[["ads", L("إعلاناتي", "My ads")], ["drafts", `${L("المسودات", "Drafts")}${drafts.length ? ` (${drafts.length})` : ""}`]].map(([k, label]) => (
            <button key={k} onClick={() => setSp(k === "drafts" ? { tab: "drafts" } : {}, { replace: true })} data-testid={`my-ads-tab-${k}`}
              className={`flex-1 h-10 rounded-xl text-sm font-bold transition-colors ${tab === k ? "bg-[#1A5C3A] text-white" : "text-slate-600 hover:bg-[#F4F2EC]"}`}>{label}</button>))}
        </div>
        {tab === "drafts" ? <DraftsTab drafts={drafts} reload={loadDrafts} L={L} lang={lang} />
          : ads === null ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
            : !ads.length ? <div className="text-center py-20 text-slate-500" data-testid="no-ads"><Inbox className="w-12 h-12 mx-auto mb-3 text-slate-300" />{L("لا توجد إعلانات", "No ads yet")}</div>
              : <div className="space-y-3" data-testid="my-ads-list">{ads.map((ad) => <MyAdRow key={ad.id} ad={ad} L={L} lang={lang} on={on} />)}</div>}
      </main>
      <DiscountDialog ad={discountAd} onClose={() => setDiscountAd(null)} onSaved={load} L={L} />
      <PromoteDialog promo={promo} onClose={() => setPromo(null)} L={L} />
      <RepostDialog ad={repostAd} onClose={() => setRepostAd(null)} onDone={load} L={L} />
      {intent && <PaymentDialog req={intent} onClose={() => { setIntent(null); load(); }} />}
      <Footer />
    </div>
  );
}
